import {
  Component,
  OnInit,
  inject,
  Output,
  EventEmitter,
  signal,
  computed,
  Input,
  HostListener,
  effect,
  OnDestroy,
  Injector,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DatePickerModule } from 'primeng/datepicker';
import { BadgeModule } from 'primeng/badge';
import { ReferralQueryService, referralQueryKeys } from '../../../../services/referral';
import {
  UnifiedReferralService,
  UnifiedPatientDisplay,
  UnifiedReferralFilter,
} from '../../../../services/unified-referral.service';
import { ViewEncapsulation } from '@angular/core';
import {
  ReferralFilter,
  getReferralAttentionTooltip,
} from '@ics-referral-hub/shared-types';
import { ReferralStatus } from '../../../../models/referral.model';
import {
  getStatusDisplayText,
  getReferralStatusSeverity,
  getStatusIcon,
  getStatusColorClass,
} from '../../../../shared/utils/status-severity.util';
import { ErrorService } from '../../../../shared/services/error.service';
import { ErrorModalComponent } from '../../../../shared/components/error-modal/error-modal.component';
import { FilterDialogComponent } from '../../../../components/filter-dialog/filter-dialog.component';
import {
  PageHeaderComponent,
  PageContentComponent,
  FilterButtonComponent,
  CreateButtonComponent,
  RefreshButtonComponent,
} from '../../../../shared/components';
import { AuthService } from '../../../../auth/services/auth.service';
import { LayoutService } from '../../../../services/layout.service';
import { UserPreferenceService } from '../../../../services/user-preference.service';
import { Logger } from '../../../../shared/utils/logger.util';
import { FilterChipsComponent } from '../../../../components/filter-chips/filter-chips.component';
import { UserDisplayComponent } from '../../../../shared/components/user-display/user-display.component';
import { UserDisplayService } from '../../../../services/user-display.service';

@Component({
  selector: 'app-referral-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    CardModule,
    ProgressSpinnerModule,
    SelectModule,
    TagModule,
    TooltipModule,
    DatePickerModule,
    BadgeModule,
    ErrorModalComponent,
    FilterDialogComponent,
    FilterChipsComponent,
    PageHeaderComponent,
    PageContentComponent,
    FilterButtonComponent,
    CreateButtonComponent,
    RefreshButtonComponent,
    UserDisplayComponent,
  ],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="referral-list-container">
      <!-- Page Header - only show when not embedded in dashboard -->
      <app-page-header
        *ngIf="showHeader"
        [title]="headerTitle()"
      >
        <app-refresh-button
          [iconOnly]="false"
          [loading]="loading()"
          (onClick)="loadReferrals()"
        ></app-refresh-button>
        <app-filter-button
          (onClick)="openFilterDialog()"
          [hasActiveFilters]="hasAnyActiveFilters()"
        ></app-filter-button>
        @if (authService.userPermissions()?.canCreateReferrals) {
          <app-create-button
            [label]="'NEW REFERRAL'"
            [routerLink]="'/referrals/create'"
          ></app-create-button>
        }
      </app-page-header>

      <!-- Filter Dialog -->
      <app-filter-dialog
        [visible]="filterDialogVisible"
        [currentFilters]="appliedFilters()"
        (filtersApplied)="onFiltersApplied($event)"
        (closed)="onFilterDialogClosed()"
      ></app-filter-dialog>

      <!-- Referrals Content -->
      <app-page-content>
        <!-- Active filter chips - rendered here so they share the same width as the table -->
        <app-filter-chips></app-filter-chips>
        @if (listLoadState(); as state) {
          <div class="list-load-error" role="alert">
            <i class="pi pi-exclamation-circle"></i>
            <span>
              @if (state === 'retrying') {
                Failed to load referrals. Retrying…
              } @else {
                Failed to load referrals. Check your connection and try again.
              }
            </span>
            @if (state === 'failed') {
              <button
                pButton
                type="button"
                label="Try again"
                icon="pi pi-refresh"
                class="p-button-text p-button-sm"
                (click)="loadReferrals()"
              ></button>
            }
          </div>
        }
        <div class="table-container">
          @if (externalRefreshing) {
            <div class="table-refresh-indicator">
              <i class="pi pi-refresh pi-spin"></i>
              <span>Refreshing referrals...</span>
            </div>
          }
          <p-table
            [loading]="loading()"
            [value]="referrals()"
            [paginator]="true"
            [lazy]="enableLazyLoading"
            [rows]="pageSize()"
            [totalRecords]="totalReferrals()"
            [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} referrals"
            [rowsPerPageOptions]="[10, 25, 50]"
            paginatorDropdownAppendTo="body"
            [sortField]="currentSortField()"
            [sortOrder]="currentSortOrder()"
            [stripedRows]="true"
            [tableStyle]="{ margin: '0' }"
            (onLazyLoad)="enableLazyLoading ? onLazyLoad($event) : null"
            (onSort)="enableLazyLoading ? onSort($event) : null"
          >
            <ng-template pTemplate="header">
              <tr>
                <th class="attention-col">
                  <span class="sr-only">Needs attention</span>
                </th>
                <th pSortableColumn="referralId">
                  Referral ID <p-sortIcon field="referralId"></p-sortIcon>
                </th>
                <th pSortableColumn="patientName">
                  Patient Name <p-sortIcon field="patientName"></p-sortIcon>
                </th>
                <th pSortableColumn="hospitalName">
                  Hospital Name <p-sortIcon field="hospitalName"></p-sortIcon>
                </th>
                <th pSortableColumn="programName">
                  Program Name <p-sortIcon field="programName"></p-sortIcon>
                </th>
                <th pSortableColumn="referredDate">
                  Referred Date <p-sortIcon field="referredDate"></p-sortIcon>
                </th>
                <th>Assignment</th>
                <th pSortableColumn="status">
                  Status <p-sortIcon field="status"></p-sortIcon>
                </th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-referral>
              <tr
                (click)="navigateToReferral(referral)"
                style="cursor: pointer;"
              >
                <td class="attention-col">
                  @if (referral.attentionReasons?.length) {
                    <i
                      class="pi pi-exclamation-triangle attention-icon"
                      [pTooltip]="getAttentionTooltip(referral)"
                      tooltipPosition="right"
                      [attr.aria-label]="getAttentionTooltip(referral)"
                      role="img"
                    ></i>
                  }
                </td>
                <td>
                  <span
                    class="referral-id"
                    [class.form-link]="referral.source === 'referral-form'"
                    [pTooltip]="referral.referralId"
                    tooltipPosition="top"
                  >
                    {{ formatReferralId(referral.referralId) }}
                    <span
                      *ngIf="referral.source === 'referral-form'"
                      class="source-indicator"
                      pTooltip="Referral Form"
                    >
                      📝
                    </span>
                  </span>
                </td>
                <td>
                  <div
                    class="patient-cell"
                    [pTooltip]="referral.patientName"
                    tooltipPosition="top"
                  >
                    <i class="pi pi-user patient-icon"></i>
                    <span>{{ referral.patientName }}</span>
                  </div>
                </td>
                <td>
                  <span
                    class="b-pill b-pill-hospital"
                    [pTooltip]="referral.hospitalName"
                    tooltipPosition="top"
                  >
                    {{ referral.hospitalName }}
                  </span>
                </td>
                <td>
                  <span
                    class="b-pill b-pill-program"
                    [ngClass]="{
                      'type-tcu': referral.programType === 'TCU',
                      'type-community': referral.programType === 'Community',
                    }"
                    [pTooltip]="referral.programName"
                    tooltipPosition="top"
                  >
                    {{ referral.programName }}
                  </span>
                </td>
                <td>{{ referral.referredDate | date: 'shortDate' }}</td>
                <td (click)="$event.stopPropagation()">
                  @if (referral.assignedTo) {
                    <app-user-display
                      [userId]="referral.assignedTo"
                      variant="compact"
                      size="sm"
                      nameFormat="first-last-initial"
                    ></app-user-display>
                  } @else {
                    <span class="assignment-unassigned">—</span>
                  }
                </td>
                <td style="text-align: right;">
                  <div class="status-cell">
                    <div
                      class="b-status-badge"
                      [ngClass]="getStatusColorClass(referral.status)"
                    >
                      <i
                        class="pi"
                        [ngClass]="getStatusIcon(referral.status)"
                      ></i>
                      {{ getStatusDisplayText(referral.status) }}
                    </div>
                  </div>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="8">
                  @if (listLoadState()) {
                    <div class="table-empty-state">
                      <i class="pi pi-exclamation-circle table-empty-icon"></i>
                      <p class="table-empty-title">Referrals could not be loaded</p>
                      <p class="table-empty-subtitle">
                        Check your connection and try again.
                      </p>
                    </div>
                  } @else {
                  <div class="table-empty-state">
                    <i class="pi pi-inbox table-empty-icon"></i>
                    <p class="table-empty-title">No referrals found</p>
                    <p class="table-empty-subtitle">
                      @if (authService.userPermissions()?.canCreateReferrals) {
                        Try adjusting your filters or create a new referral.
                      } @else {
                        Try adjusting your filters or check back later for new
                        referrals.
                      }
                    </p>
                    @if (canSearchArchived()) {
                      <button
                        pButton
                        type="button"
                        label="Search archived referrals"
                        class="p-button-text p-button-sm"
                        icon="pi pi-history"
                        (click)="searchIncludingArchived()"
                      ></button>
                    }
                  </div>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      </app-page-content>

      <!-- Error Modal -->
      <app-error-modal
        [visible]="errorService.isModalVisible()"
        [errorMessage]="errorService.errorMessage()"
        (close)="errorService.hideError()"
      ></app-error-modal>
    </div>
  `,
  styles: [
    `
      .referral-list-container {
        background: var(--background-color);
        min-height: 100vh;
      }

      .table-empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 3rem 1rem;
        color: var(--text-muted);
        text-align: center;

        .table-empty-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
          opacity: 0.4;
        }

        .table-empty-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--text-color-dark);
          margin: 0 0 0.5rem 0;
        }

        .table-empty-subtitle {
          font-size: 0.9rem;
          margin: 0;
        }
      }

      .list-load-error {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        max-width: var(--max-content-width);
        margin: 0 auto 0.75rem;
        padding: 0.5rem 1rem;
        border: 1px solid var(--error-color);
        border-radius: var(--border-radius);
        background: var(--background-card);
        color: var(--error-color);
      }

      .table-container {
        max-width: var(--max-content-width);
        margin: 0 auto;
        background: var(--background-card);
        border: 1px solid var(--border-color);
        border-radius: var(--border-radius);
        overflow: hidden;
      }

      .loading-container {
        text-align: center;
        padding: 2rem;
      }

      .loading-container p {
        margin-top: 1rem;
        color: var(--text-muted);
      }

      .referral-link {
        color: var(--primary-color);
        text-decoration: none;
        font-weight: 500;
      }

      .referral-link:hover {
        text-decoration: underline;
      }

      /* Narrow left gutter holding the "needs attention" indicator. Kept on the
         left edge so a full page of rows can be scanned in a single pass. */
      .attention-col {
        width: 2.5rem;
        min-width: 2.5rem;
        padding-right: 0 !important;
        text-align: center;
      }

      /* Same token the referral detail page uses for "action needed" copy, so the
         list indicator and the detail card read as one signal. */
      .attention-icon {
        color: var(--status-waitlisted);
        font-size: 1rem;
        vertical-align: middle;
      }

      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      .referral-id {
        color: var(--primary-color);
        font-weight: 500;
        text-decoration: underline;
        cursor: pointer;
      }

      .referral-id:hover {
        color: var(--btn-primary-bg-hover, #3c697c);
      }

      .form-link {
        color: var(--primary-color);
        font-family: monospace;
      }

      .source-indicator {
        margin-left: 0.5rem;
        font-size: 0.8rem;
        opacity: 0.7;
      }

      .patient-cell {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        max-width: 200px;
        overflow: hidden;

        .patient-icon {
          color: var(--primary-color);
          font-size: 1rem;
          flex-shrink: 0;
        }

        span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      .remain-days {
        display: block;
        font-size: 0.8rem;
        color: var(--text-muted);
        margin-top: 0.25rem;
      }

      .assignment-unassigned {
        color: var(--text-muted);
      }

      .status-cell {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
      }
    `,
  ],
})
export class ReferralListComponent implements OnInit, OnDestroy {
  @Input() enableLazyLoading: boolean = true;
  @Input() showHeader: boolean = true;
  @Input() externalRefreshing: boolean = false;
  @Output() totalReferralsChanged = new EventEmitter<number>();

  // Filter dialog state
  filterDialogVisible = false;
  readonly appliedFilters = computed(() => this.referralQueryService.filters());
  private hasInitializedFilterSync = false;
  private lastLoadedFilterKey = '';

  hasAnyActiveFilters(): boolean {
    const f = this.appliedFilters();
    return !!(
      f.status?.length ||
      f.referralId ||
      f.patientName ||
      f.hospitalIds?.length ||
      f.hospitalId ||
      f.programId ||
      f.programType ||
      f.startDate ||
      f.endDate ||
      f.archivedFilter ||
      f.assignedTo ||
      f.needsAttention
    );
  }

  readonly canSearchArchived = computed(() => {
    const f = this.appliedFilters();
    if (f.archivedFilter === 'all') return false;
    if (f.patientName) return false; // name search already includes archived
    return !!(
      f.referralId ||
      f.hospitalIds?.length ||
      f.hospitalId ||
      f.programId ||
      f.programType ||
      f.startDate ||
      f.endDate ||
      f.status?.length
    );
  });

  searchIncludingArchived(): void {
    this.referralQueryService.setFilters({
      ...this.appliedFilters(),
      archivedFilter: 'all',
    });
  }

  getReferralStatusSeverity = getReferralStatusSeverity;
  getStatusDisplayText = getStatusDisplayText;
  getStatusIcon = getStatusIcon;
  getStatusColorClass = getStatusColorClass;

  private router = inject(Router);
  protected authService = inject(AuthService);
  public errorService = inject(ErrorService);
  private unifiedReferralService = inject(UnifiedReferralService);
  private referralQueryService = inject(ReferralQueryService);
  layoutService = inject(LayoutService);
  private userPreferenceService = inject(UserPreferenceService);
  private userDisplayService = inject(UserDisplayService);
  private injector = inject(Injector);

  // Pagination and sorting state (signals so the list query reacts to changes).
  // Seeded from persisted preferences here (rather than in ngOnInit) so the
  // very first list fetch already uses the user's saved page size/sort.
  private readonly persistedPagination = this.userPreferenceService.getPreference<{
    pageSize: number;
  }>(UserPreferenceService.KEYS.REFERRAL_LIST_PAGINATION);
  private readonly persistedSort = this.userPreferenceService.getPreference<{
    field: string;
    order: number;
  }>(UserPreferenceService.KEYS.REFERRAL_LIST_SORT);

  pageSize = signal(this.persistedPagination?.pageSize ?? 10);
  currentPage = signal(0);
  currentSortField = signal(
    this.mapSortFieldToUi(this.persistedSort?.field ?? 'referredDate'),
  );
  currentSortOrder = signal(this.persistedSort?.order ?? -1);

  /** Reactive filter passed to the unified list query — recomputes on any filter/page/sort change */
  private readonly unifiedFilter = computed<UnifiedReferralFilter>(() =>
    this.buildUnifiedFilter(this.appliedFilters(), {
      page: this.currentPage(),
      pageSize: this.pageSize(),
      sortField: this.currentSortField(),
      sortOrder: this.currentSortOrder(),
    }),
  );

  private readonly listQuery = injectQuery(() => ({
    queryKey: referralQueryKeys.list(this.unifiedFilter() as ReferralFilter),
    queryFn: () =>
      firstValueFrom(
        this.unifiedReferralService.getUnifiedPatientDisplayData(this.unifiedFilter()),
      ),
    // Reasonable cache window so navigating away/back doesn't always re-hit the API,
    // while refetchOnWindowFocus (below) still picks up referrals created in other
    // sessions/tabs as soon as the user comes back to this one.
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  }));

  readonly referrals = computed<UnifiedPatientDisplay[]>(
    () => this.listQuery.data()?.referrals ?? [],
  );
  readonly loading = computed(
    () => this.listQuery.isLoading() || this.listQuery.isFetching(),
  );
  readonly totalReferrals = computed(() => this.listQuery.data()?.total ?? 0);

  /**
   * 'retrying' while a failed list fetch is being retried, 'failed' once it has
   * given up, otherwise null. Shown from the first failure so the user isn't
   * left looking at a silent spinner or an empty list (MYB-5197).
   */
  readonly listLoadState = computed<'retrying' | 'failed' | null>(() => {
    if (this.listQuery.isFetching() && this.listQuery.failureCount() > 0) return 'retrying';
    if (this.listQuery.isError()) return 'failed';
    return null;
  });

  /** Referrals matching the current filters that still need attention */
  readonly needsAttentionTotal = computed(
    () => this.listQuery.data()?.needsAttentionTotal ?? 0,
  );

  readonly headerTitle = computed(() => {
    const base = `Referrals (${this.totalReferrals()})`;
    const needsAttention = this.needsAttentionTotal();
    if (needsAttention === 0) {
      return base;
    }
    const verb = needsAttention === 1 ? 'needs' : 'need';
    return `${base} · ${needsAttention} ${verb} attention`;
  });

  getAttentionTooltip(referral: UnifiedPatientDisplay): string {
    return getReferralAttentionTooltip(referral.attentionReasons);
  }

  private totalReferralsEmitEffect = effect(
    () => this.totalReferralsChanged.emit(this.totalReferrals()),
    { injector: this.injector },
  );

  private preloadAssigneesEffect = effect(
    () => {
      const referrals = this.referrals();
      const assignmentUserIds = [
        ...new Set(
          referrals
            .map((referral) => referral.assignedTo)
            .filter((userId): userId is string => !!userId),
        ),
      ].sort();
      if (assignmentUserIds.length > 0) {
        void this.userDisplayService.preloadUsers(assignmentUserIds);
      }
    },
    { injector: this.injector },
  );

  private filterSyncEffect = effect(
    () => {
      const filters = this.appliedFilters();
      const filterKey = this.getCoreFilterKey(filters);

      if (!this.hasInitializedFilterSync) {
        this.hasInitializedFilterSync = true;
        this.lastLoadedFilterKey = filterKey;
        return;
      }

      if (filterKey === this.lastLoadedFilterKey) {
        return;
      }

      this.lastLoadedFilterKey = filterKey;
      this.currentPage.set(0);
    },
    { injector: this.injector }
  );

  ngOnInit(): void {
    this.layoutService.setMaxWidth('large');

    // Filter persistence is now handled by ReferralQueryService.
    this.userPreferenceService.removePreference(
      UserPreferenceService.KEYS.REFERRAL_LIST_FILTERS,
    );

    // The list query (see `listQuery` field) fetches automatically as soon as the
    // component is constructed, driven by the reactive `unifiedFilter` — no manual
    // trigger needed here for either lazy or non-lazy p-table usage.
  }

  ngOnDestroy(): void {
    this.layoutService.resetWidth();
  }

  openFilterDialog(): void {
    this.filterDialogVisible = true;
  }

  onFilterDialogClosed(): void {
    this.filterDialogVisible = false;
  }

  onFiltersApplied(filters: any): void {
    this.filterDialogVisible = false;
    this.referralQueryService.setFilters(filters);
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    if (this.filterDialogVisible) {
      this.filterDialogVisible = false;
      event.preventDefault();
    }
  }

  loadReferrals() {
    void this.listQuery.refetch();
  }

  private buildUnifiedFilter(
    appliedFilters: ReferralFilter & { startDate?: Date | string; endDate?: Date | string },
    pagination: { page: number; pageSize: number; sortField: string; sortOrder: number },
  ): UnifiedReferralFilter {
    const filter: UnifiedReferralFilter = {
      limit: pagination.pageSize,
      page: pagination.page + 1, // Backend expects 1-based page numbers
      sortBy: this.mapSortFieldToBackend(pagination.sortField),
      sortOrder: pagination.sortOrder === 1 ? 'ASC' : 'DESC',
    };

    // Apply filters from dialog
    if (appliedFilters.referralId) {
      filter.referralId = appliedFilters.referralId;
    }

    if (appliedFilters.patientName) {
      filter.patientName = appliedFilters.patientName;
    }

    if (appliedFilters.hospitalIds?.length) {
      filter.hospitalIds = appliedFilters.hospitalIds;
    } else if (appliedFilters.hospitalId) {
      filter.hospitalId = appliedFilters.hospitalId;
    }

    if (appliedFilters.programId) {
      filter.programId = appliedFilters.programId;
    }

    if (appliedFilters.programType) {
      filter.programType = appliedFilters.programType;
    }

    if (appliedFilters.status && appliedFilters.status.length > 0) {
      filter.status = appliedFilters.status;
    }

    // Add date range filtering if dates are selected
    if (appliedFilters.startDate) {
      filter.startDate =
        appliedFilters.startDate instanceof Date
          ? appliedFilters.startDate.toISOString()
          : appliedFilters.startDate;
    }

    if (appliedFilters.endDate) {
      filter.endDate =
        appliedFilters.endDate instanceof Date
          ? appliedFilters.endDate.toISOString()
          : appliedFilters.endDate;
    }

    if (appliedFilters.archivedFilter) {
      filter.archivedFilter = appliedFilters.archivedFilter;
    }

    if (appliedFilters.assignedTo) {
      filter.assignedTo = appliedFilters.assignedTo;
    }

    if (appliedFilters.needsAttention) {
      filter.needsAttention = true;
    }

    // Name search includes archived unless user limited to active only
    if (filter.patientName && appliedFilters.archivedFilter !== 'active') {
      filter.archivedFilter = 'all';
    }

    return filter;
  }

  navigateToReferral(referral: UnifiedPatientDisplay): void {
    this.router.navigate([referral.editRoute], {
      state: { referralPreview: referral },
    });
  }

  onSort(event: any): void {
    const sortField = event.field;

    Logger.log(
      'ReferralList: Sort changed - field:',
      event.field,
      'mapped to:',
      this.mapSortFieldToBackend(sortField),
      'order:',
      event.order,
    );

    // Keep PrimeNG column field names in state; map to backend in buildUnifiedFilter.
    this.currentSortField.set(sortField);
    this.currentSortOrder.set(event.order);

    // Save Sort Preference
    this.userPreferenceService.setPreference(
      UserPreferenceService.KEYS.REFERRAL_LIST_SORT,
      { field: this.currentSortField(), order: this.currentSortOrder() },
    );

    // Reset to first page when sorting changes
    this.currentPage.set(0);
  }

  onLazyLoad(event: any): void {
    this.currentPage.set(Math.floor(event.first / event.rows));
    const newPageSize = event.rows;

    // Save page size preference if it changed
    if (newPageSize !== this.pageSize()) {
      this.pageSize.set(newPageSize);
      this.userPreferenceService.setPreference(
        UserPreferenceService.KEYS.REFERRAL_LIST_PAGINATION,
        { pageSize: this.pageSize() },
      );
    }

    if (event.sortField) {
      this.currentSortField.set(event.sortField);
      this.currentSortOrder.set(event.sortOrder);

      // Save Sort Preference (redundant safe-guard if onSort doesn't trigger)
      this.userPreferenceService.setPreference(
        UserPreferenceService.KEYS.REFERRAL_LIST_SORT,
        { field: this.currentSortField(), order: this.currentSortOrder() },
      );
    }
  }

  formatReferralId(id: string): string {
    if (!id || id.length <= 22) return id;
    const segments = id.split('-');
    return `${segments[0]}-${segments[1]}…`;
  }

  private getCoreFilterKey(
    filters: ReferralFilter & {
      limit?: unknown;
      offset?: unknown;
      page?: unknown;
      sortBy?: unknown;
      sortOrder?: unknown;
    },
  ): string {
    const { limit, offset, page, sortBy, sortOrder, ...coreFilters } = filters;
    return JSON.stringify(coreFilters);
  }

  /** Map PrimeNG column field names to backend sort fields. */
  private mapSortFieldToBackend(sortField: string): string {
    const fieldMapping: Record<string, string> = {
      patientName: 'patient.name.0.family',
      hospitalName: 'hospital.name',
      programName: 'program.name',
      status: 'status',
    };

    return fieldMapping[sortField] ?? sortField;
  }

  /** Map persisted backend sort fields back to PrimeNG column field names. */
  private mapSortFieldToUi(sortField: string): string {
    const fieldMapping: Record<string, string> = {
      'patient.name.0.family': 'patientName',
      'hospital.name': 'hospitalName',
      'program.name': 'programName',
    };

    return fieldMapping[sortField] ?? sortField;
  }
}
